import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";
import { Button } from "@/components/ui/button";
import { UmbrellaLogo } from "@/components/UmbrellaLogo";
import BusinessCard from "@/components/BusinessCard";
import { ShieldCheck, BadgeCheck, Umbrella, ArrowRight, Star, Search, Sparkles } from "lucide-react";

const HERO_RESIDENTIAL = "https://images.unsplash.com/photo-1687422810663-c316494f725a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const HERO_COMMERCIAL = "https://images.unsplash.com/photo-1787672357497-9d9e025f01b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";


export default function Landing() {
  usePageMeta("Home", "Hire vetted Black-owned contractors and small businesses across Canada. Get custom quotes and pay safely through escrow.");
  const [featured, setFeatured] = useState([]);
  const [featuredStrip, setFeaturedStrip] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [allTrades, setAllTrades] = useState([]);
  const [liveTrades, setLiveTrades] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/businesses").then((r) => {
      setFeatured(r.data.slice(0, 3));
      setLiveTrades(new Set(r.data.flatMap((b) => b.trades || [])));
    }).catch(() => {});
    api.get("/meta").then((r) => setAllTrades(r.data.trades || [])).catch(() => {});
    api.get("/featured/businesses?limit=6").then((r) => setFeaturedStrip(r.data || [])).catch(() => {});
    api.get("/spotlight").then((r) => setSpotlight(r.data && r.data.id ? r.data : null)).catch(() => {});
  }, []);

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative brand-grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 bg-amber-500/10 px-3 py-1.5 rounded-full">
              <UmbrellaLogo size={14} /> Built for the Black community · Canada
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 leading-[1.05] text-balance">
              Hire vetted <span className="text-amber-600">Black-owned</span> trades & businesses.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-lg">
              Post a job, compare custom quotes, and pay securely into escrow. Every business is manually vetted, bonded, and insured — coverage you can trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-post-job-button" onClick={() => navigate("/post-job")}
                className="h-12 px-6 rounded-full bg-slate-950 hover:bg-amber-600 text-white text-base font-semibold">
                Post a Job <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button data-testid="hero-browse-button" onClick={() => navigate("/browse")} variant="outline"
                className="h-12 px-6 rounded-full border-slate-300 text-base font-semibold">
                <Search size={18} className="mr-1" /> Browse Businesses
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              {[["Vetted", ShieldCheck, "text-amber-600"], ["Bonded", BadgeCheck, "text-emerald-600"], ["Insured", Umbrella, "text-blue-600"]].map(([l, I, c]) => (
                <div key={l} className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                  <I size={16} className={c} /> {l}
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img data-testid="hero-residential-image" src={HERO_RESIDENTIAL} alt="Men and women renovating a residential home" className="rounded-3xl shadow-2xl object-cover w-full h-[420px]" />
            <img data-testid="hero-commercial-image" src={HERO_COMMERCIAL} alt="Men and women at work on a commercial construction site" className="absolute -bottom-8 -right-6 w-48 sm:w-56 h-40 sm:h-44 rounded-2xl shadow-2xl object-cover border-4 border-white hidden sm:block" />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden sm:block">
              <div className="flex items-center gap-1 text-amber-500"><Star size={16} className="fill-amber-500" /><Star size={16} className="fill-amber-500" /><Star size={16} className="fill-amber-500" /><Star size={16} className="fill-amber-500" /><Star size={16} className="fill-amber-500" /></div>
              <p className="text-xs text-slate-500 mt-1">Residential & commercial · strictly vetted</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-950 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center">How Black Umbrella works</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {[
              ["1", "Post your job", "Describe the work, add photos, set a budget range and your service area — free."],
              ["2", "Compare quotes", "Vetted Black-owned businesses in your area send custom quotes. Pick the best."],
              ["3", "Pay into escrow", "Funds are held safely in CAD and released only when the job is done right."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-2xl bg-white/5 border border-white/10 p-8">
                <div className="h-11 w-11 rounded-xl bg-amber-500 text-slate-950 font-heading font-extrabold text-lg flex items-center justify-center">{n}</div>
                <h3 className="mt-5 font-heading font-bold text-xl">{t}</h3>
                <p className="mt-2 text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trades */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <h2 className="font-heading text-3xl font-bold text-slate-950">Every trade, one umbrella</h2>
        <div className="mt-8 flex flex-wrap gap-3">
          {allTrades.filter((t) => liveTrades?.has(t)).map((t) => (
            <Link key={t} to={`/browse?trade=${encodeURIComponent(t)}`} data-testid={`trade-chip-${t}`}
              className="px-5 py-2.5 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-amber-400 hover:text-amber-700 transition-colors">
              {t}
            </Link>
          ))}
        </div>
        {liveTrades && allTrades.some((t) => !liveTrades.has(t)) && (
          <div data-testid="coming-soon-strip" className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Coming soon</p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-3">
              {allTrades.filter((t) => !liveTrades.has(t)).map((t) => (
                <div key={t} data-testid={`coming-soon-${t}`} className="text-sm">
                  <span className="font-semibold text-slate-700">{t}</span>
                  <Link to="/for-businesses" data-testid={`coming-soon-join-${t}`} className="ml-2 text-amber-700 hover:underline">Are you in this trade? Join Black Umbrella</Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Featured strip (paid placement) */}
      {featuredStrip.length > 0 && (
        <section className="bg-gradient-to-b from-amber-50/70 to-white border-y border-amber-100" data-testid="featured-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
            <div className="flex items-end justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                  <Sparkles size={14} /> Featured
                </span>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-950">Featured businesses</h2>
                <p className="mt-2 text-slate-600 max-w-xl">Vetted Black-owned pros getting extra visibility this week.</p>
              </div>
              <Link to="/browse" className="hidden sm:inline text-sm font-semibold text-amber-600 hover:underline whitespace-nowrap">Browse all →</Link>
            </div>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredStrip.map((b) => <BusinessCard key={b.id} b={b} />)}
            </div>
          </div>
        </section>
      )}

      {/* Community Spotlight */}
      {spotlight && (
        <section className="bg-amber-50 border-y border-amber-100" data-testid="community-spotlight">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Community Spotlight</span>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-950">This week: {spotlight.business_name}</h2>
              <p className="mt-3 text-slate-600 max-w-md">{spotlight.bio || "A vetted Black-owned business making an impact in the community."}</p>
              <Link to={`/business/${spotlight.id}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline">View profile →</Link>
            </div>
            <BusinessCard b={spotlight} />
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-3xl font-bold text-slate-950">Featured businesses</h2>
            <Link to="/browse" className="text-sm font-semibold text-amber-600 hover:underline">View all →</Link>
          </div>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((b) => <BusinessCard key={b.id} b={b} />)}
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-950">
            <UmbrellaLogo size={22} /> <span className="font-heading font-bold">Black Umbrella</span>
          </div>
          <p className="text-xs text-slate-400">Launching in the Greater Toronto Area · Open across Canada · Payments in CAD</p>
          <div className="flex items-center gap-5">
            <Link to="/for-businesses" data-testid="footer-link-for-businesses" className="text-xs font-semibold text-slate-600 hover:text-amber-600">For Businesses</Link>
            <Link to="/fees" data-testid="footer-link-fees" className="text-xs font-semibold text-slate-600 hover:text-amber-600">Fees &amp; Payments</Link>
            <Link to="/terms" data-testid="footer-link-terms" className="text-xs font-semibold text-slate-600 hover:text-amber-600">Terms</Link>
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-xs font-semibold text-slate-600 hover:text-amber-600">Privacy</Link>
            <a href="mailto:blackumbrellagta@gmail.com" data-testid="footer-contact-email" className="text-xs font-semibold text-slate-600 hover:text-amber-600">blackumbrellagta@gmail.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
